/**
 * The icon set, as path data.
 *
 * `inbox` and `history` left with ADR 0009 — they were the referral inbox's and
 * my-referrals' nav glyphs. `send` and `chevron-down` are declared and
 * unreferenced, and were before that change; they are left for whoever needs a
 * paper plane or a disclosure caret.
 *
 * Hand-drawn rather than a dependency: the shell needs about twenty glyphs, an
 * icon package is a few hundred kilobytes and a license to audit, and DESIGN.md
 * specifies no icon family. Every glyph is a 24×24 stroke drawing on
 * `currentColor`, so color comes from a Layer 2 token at the point of use and
 * never from here.
 *
 * Icons are never the only label. DESIGN.md section 8: status must not rely on
 * color alone, and the same argument applies to shape — `MpIcon` is
 * `aria-hidden` and every icon-only control carries an accessible name.
 */

export const ICON_PATHS = {
  home: 'M3 11.5 12 4l9 7.5V19a2 2 0 0 1-2 2h-4v-6H9v6H5a2 2 0 0 1-2-2z',
  clients:
    'M16 20v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9.5 4.5a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM17 4.8a3 3 0 0 1 0 5.9M21 20v-1a4 4 0 0 0-3-3.9',
  search: 'M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM15.5 15.5 21 21',
  // A heart with a pulse through it: Programs, which are care, as against
  // `org`'s building, which is the company that runs them.
  programs:
    'M12 20.5C9 18.6 3 14.6 3 9.2A4.7 4.7 0 0 1 12 7a4.7 4.7 0 0 1 9 2.2c0 5.4-6 9.4-9 11.3zM3.5 13H8l1.5-2.5 2.5 5 2-4 1.2 1.5h4.3',
  org: 'M4 21V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15M14 10h4a2 2 0 0 1 2 2v9M3 21h18M7.5 8h3M7.5 12h3M7.5 16h3',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  billing: 'M2.5 9.5h19M4.5 6h15a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2zM6 14h4',
  approvals: 'M12 3l7.5 3v5.5c0 4.4-3.1 8.3-7.5 9.5-4.4-1.2-7.5-5.1-7.5-9.5V6zM8.75 12l2.25 2.25L15.5 9.75',
  flag: 'M5 21V4M5 4h9l-1.5 3.5L14 11H5',
  audit: 'M7 3h8l4 4v14H7zM15 3v4h4M10 12h6M10 16h6',
  console: 'M4 7h10M18 7h2M4 12h2M10 12h10M4 17h12M20 17h0M16 5v4M8 10v4M14 15v4',
  overview: 'M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z',
  moderation: 'M12 4.5 3.5 19h17zM12 10v4M12 16.5v.5',
  more: 'M6 12h.5M12 12h.5M18 12h.5',
  sun: 'M12 6.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11zM12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9 6.3 6.3M17.7 17.7l1.4 1.4M19.1 4.9 17.7 6.3M6.3 17.7 4.9 19.1',
  moon: 'M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z',
  system: 'M4 5h16v10H4zM9 19h6M12 15v4',
  'chevron-down': 'M6 9.5l6 6 6-6',
  'chevron-up': 'M6 14.5l6-6 6 6',
  'chevron-right': 'M9.5 6l6 6-6 6',
  close: 'M6 6l12 12M18 6 6 18',
  check: 'M5 12.5 9.5 17 19 7',
  alert: 'M12 4.5 3.5 19h17zM12 10v4M12 16.5v.5',
  info: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM12 11v6M12 7.5v.5',
  external: 'M14 4h6v6M20 4l-8.5 8.5M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4',
  refresh: 'M20 11a8 8 0 1 0-2.3 5.7M20 5v6h-6',
  send: 'M4 12 20 4l-3 16-5.5-5zM11.5 15l-1 4.5 3-3.2',
  // The program listing's contact card and key facts.
  phone: 'M5 4h3.5l1.5 4-2.2 1.4a11 11 0 0 0 6.8 6.8L16 14l4 1.5V19a1.5 1.5 0 0 1-1.6 1.5A15.5 15.5 0 0 1 3.5 5.6 1.5 1.5 0 0 1 5 4z',
  mail: 'M3.5 6h17v12h-17zM3.5 6.5 12 13l8.5-6.5',
  pin: 'M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11zM12 7.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z',
  globe: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM3.5 12h17M12 3.5c2.3 2.3 3.5 5.2 3.5 8.5s-1.2 6.2-3.5 8.5c-2.3-2.3-3.5-5.2-3.5-8.5s1.2-6.2 3.5-8.5z',
  clock: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM12 7.5V12l3 2',
  calendar: 'M4.5 6h15v14h-15zM4.5 10h15M8.5 3.5v4M15.5 3.5v4',
  // The company page's facts strip and basic information.
  layers: 'M12 3.5 21 8l-9 4.5L3 8zM3 12l9 4.5 9-4.5M3 16l9 4.5 9-4.5',
  shield: 'M12 3l7.5 3v5.5c0 4.4-3.1 8.3-7.5 9.5-4.4-1.2-7.5-5.1-7.5-9.5V6z',
  user: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4.5 20.5v-.5a6 6 0 0 1 6-6h3a6 6 0 0 1 6 6v.5',
  clipboard: 'M9 4.5h6v2.5H9zM15 5.5h2.5A1.5 1.5 0 0 1 19 7v12.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19.5V7a1.5 1.5 0 0 1 1.5-1.5H9M9 12h6M9 16h4',
  signout:'M13 4H6.5A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20H13M16.5 8.5 20 12l-3.5 3.5M20 12h-9'
} as const

export type IconName = keyof typeof ICON_PATHS
